import LoadingIndicator from "../components/LoadingIndicator";
import { useContext, useEffect, useState, useMemo } from "react";
import { retrievePaymentStatus } from "../apis";
import { appearGlobalError } from "../requests/globalRequest";
import { GlobalContextStore } from "../context/GlobalProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { gaPurchase, gaTransactionFailed } from "../utils/ga";
import { gtmPurchase, gtmTransactionFailed } from "../utils/gtm";
import { formatQuoteProductForGA } from "../utils/common";
import { PROFILE_TABS, PROFILE_TAB_KEY } from "./Profile";

const ProcessingPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { globalDispatch } = useContext(GlobalContextStore);
  const [repeater, setRepeater] = useState(0);
  const maxRepeat = 10;

  // Memoize query params to prevent infinite re-renders
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tid = query.get("tid");
  const results = query.get("results");

  const getProductGAPayload = (item) => {
    return {
      id: item?.product?.productSku,
      productName: item?.product?.name,
      sellingPrice: item?.discountedPricePerItem,
      markedPrice: item?.originalPricePerItem,
      quantity: item?.quantity,
      brandName: item?.product?.brandName,
      productCategory: item?.product?.productCategory,
      variant: {
        options: item?.product?.selectedOptions?.map((variantItem) => ({
          optionKey: variantItem?.option?.optionKey,
          valueKey: variantItem?.value?.value
        })) || [],
        markedPrice: item?.originalPricePerItem,
        sellingPrice: item?.discountedPricePerItem,
      },
      paidVas: item?.paidVas,
      freeVas: item?.freeVas,
      isAddedFromRecommend: item?.isAddedFromRecommend,
    }
  }

  const getPlanGAPayload = (item) => {
    return {
      ...item,
      ...item?.plan,
      planName: item?.plan?.name,
      variant: item?.plan?.contractPeriod,
    }
  }

  useEffect(() => {
    let timeoutId = null; // For cleanup

    const fetcher = async () => {
      try {
        const body = { result: results };
        const result = await retrievePaymentStatus(body, tid);
        
        // Check if the API call was successful and transaction status is SUCCESS
        if (result?.msg === "success" && result?.data?.transactionStatus === "SUCCESS") {
          // Stop retrying
          setRepeater(maxRepeat);
          
          // Handle GA
          const gaPayload = result?.data?.orderItems
                              .filter((item) => item.type !== "delivery")
                              .map((item) => 
                                (item?.type === "plan") ? getPlanGAPayload(item) : getProductGAPayload(item)
                              );

          const transactionInfo = {
            transaction_id: result?.data?.orderNumber || "NA",
            tax: result?.data?.tax || 0,
            coupon: result?.data?.promoCode || "NA",
            shipping: result?.data?.deliveryFee || 0,
            value: (result?.data?.orderItemsTotalAmount || 0) - (result?.data?.promoCodeDiscountAmount || 0),
          }

          gaPurchase(gaPayload, transactionInfo);
          gtmPurchase(gaPayload, transactionInfo);

          // check contain plan type item 
          const isContainPlan = result?.data?.orderItems.some((item) => item?.type === "plan");
          if (isContainPlan) {
            localStorage.setItem("isAfterPayment", "true"); // for pop up hint dialog
          } else {
            localStorage.setItem(PROFILE_TAB_KEY, PROFILE_TABS.ORDER_DETAILS);
          }
          
          navigate("/profile");
        } else {
          // Transaction not successful yet or still processing - retry
          if (result?.data?.transactionStatus && result?.data?.transactionStatus !== "SUCCESS") {
            gaTransactionFailed(result?.data?.transactionStatus);
            gtmTransactionFailed(result?.data?.transactionStatus);
          }
          
          // Retry after 5 seconds if not reached max attempts
          if (repeater < maxRepeat - 1) {
            timeoutId = setTimeout(() => setRepeater((prevState) => prevState + 1), 5000);
          } else {
            // Max retries reached, navigate to profile
            navigate("/profile");
          }
        }
      } catch (e) {
        console.log("process payment error - ", e);
        const errorMessage = e?.errorMessage || e?.message || "Unknown error";
        gaTransactionFailed(errorMessage);
        gtmTransactionFailed(errorMessage);
        
        // Retry after 5 seconds if not reached max attempts
        if (repeater < maxRepeat - 1) {
          timeoutId = setTimeout(() => setRepeater((prevState) => prevState + 1), 5000);
        } else {
          // Max retries reached, navigate to profile
          navigate("/profile");
        }
      }
    };

    if (tid && results) {
      if (repeater < maxRepeat) {
        fetcher();
      } else {
        navigate("/profile");
      }
    } else {
      navigate("/profile");
      setRepeater(maxRepeat);
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [repeater, tid, results, navigate]);

  return <LoadingIndicator />;
};

export default ProcessingPayment;
